import { GAP_DENSITY_MAP_STYLE, GAP_DENSITY_MAP_THEME_MAUVE } from "@app/projects/network-assessment/maps/gap-density-map/GapDensityMapsConstants";
import { GapDensityMapTheme } from "@app/projects/network-assessment/maps/gap-density-map/interfaces";
import { UserContext } from "@app/user/UserContext";
import { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

interface GapDensityMapThemeContextProps {
  addOrUpdateCustomTheme: (theme: GapDensityMapTheme) => void;
  currentTheme: GapDensityMapTheme,
  customThemes: GapDensityMapTheme[];
  deleteCustomTheme: (theme: GapDensityMapTheme) => void;
  getGapDensityMapColors: (bucketCount: number) => string[];
  prevEditThemeName: string,
  themeToEdit: GapDensityMapTheme;
  setTheme: (themeName: string) => void;
  setThemeToEdit: (theme: GapDensityMapTheme) => void;
  editingBucketIndex: number | null;
  setEditingBucketIndex: (idx: number | null) => void;
}

const GapDensityMapThemeContext = createContext<Partial<GapDensityMapThemeContextProps>>({});

const GapDensityMapThemeContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {

  const userContext = useContext(UserContext);

  const localStorageKeyCurrent = useRef(`${userContext.userId}__gap-density-map-theme`).current;
  const localStorageKeyCustomThemes = useRef(`${userContext.userId}__gap-density-map-custom-themes`).current;

  const [theme, setTheme] = useState<string>(GAP_DENSITY_MAP_THEME_MAUVE.uuid);
  const [customThemes, setCustomThemes] = useState<GapDensityMapTheme[]>([]);
  const [themeToEdit, setThemeToEdit] = useState<GapDensityMapTheme>(null);
  const [editingBucketIndex, setEditingBucketIndex] = useState<number | null>(null);

  const getGapDensityMapColors = (bucketCount: number): string[] => {
    if (bucketCount === GAP_DENSITY_MAP_STYLE.NUMBER_OF_BUCKETS_WITH_SPECIALTY) return [currentTheme.noGapColor, currentTheme.gapColor4];
    const themeArray = [currentTheme.noGapColor, currentTheme.gapColor1, currentTheme.gapColor2, currentTheme.gapColor3, currentTheme.gapColor4];
    return themeArray.slice(0, bucketCount);
  };

  const currentTheme = useMemo<GapDensityMapTheme>(() => {
    const currentTheme = GAP_DENSITY_MAP_STYLE.QUEST_THEMES.find(questTheme => questTheme.uuid === theme) || customThemes.find(customTheme => customTheme.uuid === theme);

    if (!!currentTheme) {
      return currentTheme;
    } else {
      setTheme(GAP_DENSITY_MAP_THEME_MAUVE.uuid);
      return GAP_DENSITY_MAP_THEME_MAUVE;
    }
  }, [theme, customThemes]);

  const setCustomThemesSorted = (themes: GapDensityMapTheme[]) => {
    setCustomThemes(themes.sort((a, b) => a.name.localeCompare(b.name)));
  }

  const addOrUpdateCustomTheme = (theme: GapDensityMapTheme): void => {
    const existingThemeIndex = customThemes.findIndex(customTheme => themeToEdit?.uuid === customTheme?.uuid);
    const updatedThemes = customThemes.map(t => ({ ...t }));
    if (existingThemeIndex === -1) {
      updatedThemes.push(theme);
    } else {
      updatedThemes[existingThemeIndex] = theme;
    }

    setCustomThemesSorted(updatedThemes);
  }

  const deleteCustomTheme = (theme: GapDensityMapTheme): void => {
    try {
      if (currentTheme.uuid === theme.uuid) {
        setTheme(GAP_DENSITY_MAP_THEME_MAUVE.uuid);
        toast.success(
          <span>Theme deleted. Default theme applied.</span>, {
            containerId: "z-index-9999",
            type: "info"
        });
      }

      const updatedThemes = customThemes.filter(customTheme => customTheme.uuid !== theme.uuid);
      setCustomThemesSorted(updatedThemes);

      if (themeToEdit?.uuid === theme.uuid) {
        setThemeToEdit(null);
      }
    } catch {
      toast.error(
        <span>Something went wrong. Please try again later.</span>, {
          containerId: "z-index-9999",
          type: "error"
      });
    }
  }

  useEffect(() => {
    const savedTheme = window.localStorage.getItem(localStorageKeyCurrent);
    if (!!savedTheme) {
      setTheme(JSON.parse(savedTheme) as string);
    }

    const savedCustomThemes = window.localStorage.getItem(localStorageKeyCustomThemes);
    if (!!savedCustomThemes) {
      setCustomThemes(JSON.parse(savedCustomThemes) as GapDensityMapTheme[]);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem(localStorageKeyCurrent, JSON.stringify(theme));
  }, [theme]);

  useEffect(() => {
    window.localStorage.setItem(localStorageKeyCustomThemes, JSON.stringify(customThemes));
  }, [customThemes]);

  const contextValue = {
    addOrUpdateCustomTheme,
    currentTheme,
    customThemes,
    deleteCustomTheme,
    editingBucketIndex,
    getGapDensityMapColors,
    setEditingBucketIndex,
    setTheme,
    setThemeToEdit,
    themeToEdit,
  }

  return (
    <GapDensityMapThemeContext value={contextValue}>
      {props.children}
    </GapDensityMapThemeContext>
  )
}

export { GapDensityMapThemeContext, GapDensityMapThemeContextProvider, GapDensityMapThemeContextProps };